import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, TextField, Typography,
  CircularProgress, Alert, Stepper, Step, StepLabel,
  InputAdornment, IconButton,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Button from '@mui/material/Button';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../reduxStore/Store';
import { registerUser, clearAuthError, clearRegisterMessage } from '../reduxStore/AuthSlice';

const STEPS = ['Société', 'Compte'];

const RegisterPage: React.FC = () => {
  const dispatch  = useDispatch<AppDispatch>();
  const navigate  = useNavigate();
  const { loading, error, isAuthenticated, registerMessage } = useSelector((state: RootState) => state.AuthSlice);

  const [step,     setStep]     = useState(0);
  const [company,  setCompany]  = useState('');
  const [login,    setLogin]    = useState('');
  const [email,    setEmail]    = useState('');
  const [phone,    setPhone]    = useState('');
  const [password, setPassword] = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [showPwd,  setShowPwd]  = useState(false);
  const [localErr, setLocalErr] = useState('');

  useEffect(() => {
    if (isAuthenticated) navigate('/', { replace: true });
  }, [isAuthenticated, navigate]);

  // Message « en attente de validation » : effacé en quittant la page.
  useEffect(() => {
    return () => { dispatch(clearRegisterMessage()); };
  }, [dispatch]);

  useEffect(() => {
    if (error) dispatch(clearAuthError());
    setLocalErr('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company, login, email, phone, password, confirm]);

  const handleNextStep = () => {
    if (!company.trim()) {
      setLocalErr('Le nom de la société est requis.');
      return;
    }
    setLocalErr('');
    setStep(1);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalErr('');

    if (!login.trim() || !email.trim() || !phone.trim() || !password) {
      setLocalErr('Tous les champs sont requis.');
      return;
    }
    if (password !== confirm) {
      setLocalErr('Les mots de passe ne correspondent pas.');
      return;
    }
    if (password.length < 6) {
      setLocalErr('Le mot de passe doit contenir au moins 6 caractères.');
      return;
    }

    dispatch(registerUser({ company: company.trim(), login: login.trim(), email: email.trim(), phone: phone.trim(), password }));
  };

  const displayedError = localErr || error;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f5f5',
      }}
    >
      <Card sx={{ width: 420, boxShadow: 4, borderRadius: 3 }}>
        <CardContent sx={{ p: 4 }}>

          {/* En-tête */}
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Box
              sx={{
                width: 52, height: 52, borderRadius: '14px',
                bgcolor: 'primary.main', display: 'flex',
                alignItems: 'center', justifyContent: 'center', mb: 1.5,
              }}
            >
              <PersonAddIcon sx={{ color: '#fff', fontSize: 28 }} />
            </Box>
            <Typography variant="h6" fontWeight={700}>Créer un compte</Typography>
            <Typography variant="caption" color="text.secondary">Gsoft Web ERP</Typography>
          </Box>

          {/* Inscription enregistrée, compte en attente de validation */}
          {registerMessage && (
            <Box>
              <Alert severity="success" sx={{ mb: 3 }}>{registerMessage}</Alert>
              <Button variant="contained" fullWidth onClick={() => navigate('/login')}>
                Retour à la connexion
              </Button>
            </Box>
          )}

          {/* Stepper */}
          {!registerMessage && (
          <Stepper activeStep={step} sx={{ mb: 3 }}>
            {STEPS.map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>
          )}

          {/* Erreur */}
          {!registerMessage && displayedError && (
            <Alert severity="error" sx={{ mb: 2 }}>{displayedError}</Alert>
          )}

          {/* Étape 1 : Société */}
          {!registerMessage && step === 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Entrez le nom exact de votre société (tel qu'enregistré par votre administrateur).
              </Typography>
              <TextField
                label="Nom de la société"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                fullWidth
                size="small"
                sx={{ mb: 3 }}
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleNextStep()}
              />
              <Button variant="contained" fullWidth onClick={handleNextStep}>
                Suivant
              </Button>
            </Box>
          )}

          {/* Étape 2 : Informations du compte */}
          {!registerMessage && step === 1 && (
            <form onSubmit={handleSubmit}>
              <TextField
                label="Identifiant (login)"
                value={login}
                onChange={(e) => setLogin(e.target.value)}
                fullWidth size="small" sx={{ mb: 2 }}
                autoFocus autoComplete="username"
              />
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth size="small" sx={{ mb: 2 }}
                autoComplete="email"
              />
              <TextField
                label="Téléphone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                fullWidth size="small" sx={{ mb: 2 }}
              />
              <TextField
                label="Mot de passe"
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth size="small" sx={{ mb: 2 }}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPwd((v) => !v)} edge="end">
                        {showPwd ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Confirmer le mot de passe"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                fullWidth size="small" sx={{ mb: 3 }}
                autoComplete="new-password"
              />
              <Box display="flex" gap={1}>
                <Button variant="outlined" onClick={() => setStep(0)} sx={{ flex: 1 }}>
                  Retour
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={loading}
                  sx={{ flex: 2 }}
                >
                  {loading ? <CircularProgress size={20} color="inherit" /> : 'Créer mon compte'}
                </Button>
              </Box>
            </form>
          )}

          {/* Lien connexion */}
          <Box textAlign="center" mt={2}>
            <Typography variant="caption" color="text.secondary">
              Déjà un compte ?{' '}
              <Link to="/login" style={{ color: 'inherit', fontWeight: 600 }}>
                Se connecter
              </Link>
            </Typography>
          </Box>

        </CardContent>
      </Card>
    </Box>
  );
};

export default RegisterPage;
